import React, { useState } from 'react';
import {
  Box,
  Button,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Grid,
} from '@mui/material';
import { ArrowLeft, ArrowRight, CalendarToday } from '@mui/icons-material';

const TimePeriod: React.FC<{
  years: number;
  date: string;
  handleSetYears: (years: number) => void;
}> = ({ years, date, handleSetYears }) => {
  const [newDate, setNewDate] = useState(() => {
    const newDate = new Date(date);
    newDate.setFullYear(newDate.getFullYear() + 1);
    return newDate.toISOString().split('T')[0];
  });

  const setupYearsInRows = () => {
    const nextYear = new Date(date);
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    const nextYearNumber = nextYear.getFullYear();
    const yearRange = Array.from({ length: 100 }, (_, i) => nextYearNumber + i);
    return yearRange.reduce<number[][]>((rows, year, index) => {
      if (index % 3 === 0) {
        rows.push([year]);
      } else {
        rows[rows.length - 1].push(year);
      }
      return rows;
    }, []);
  };

  const yearsInRows = setupYearsInRows();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const incrementYear = () => {
    setNewDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setFullYear(newDate.getFullYear() + 1);
      return newDate.toISOString().split('T')[0];
    });
    handleSetYears(years + 1);
  };

  const decrementYear = () => {
    if (years > 1) {
      setNewDate(prevDate => {
        const newDate = new Date(prevDate);
        newDate.setFullYear(newDate.getFullYear() - 1);
        return newDate.toISOString().split('T')[0];
      });
      handleSetYears(years - 1);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleYearSelect = (selectedYear: number) => {
    setNewDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setFullYear(selectedYear);
      return newDate.toISOString().split('T')[0];
    });
    handleSetYears(selectedYear - yearsInRows[0][0] + 1);
    handleClose();
  };

  return (
    <Tooltip title={`${years} year${years !== 1 ? 's' : ''}`} arrow>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          border: '1px solid',
          borderColor: 'grey.600',
          borderRadius: '8px',
          padding: '4px',
          px: 4,
        }}
      >
        <CalendarToday sx={{ color: '#9c9c9c', mr: 1 }} />
        <Button
          data-cy="decrement-year"
          onClick={decrementYear}
          sx={{ minWidth: '30px', color: '#fff' }}
          disabled={years === 1}
        >
          <ArrowLeft data-cy="arrow-left" />
        </Button>
        <Typography
          variant="body1"
          color="text.primary"
          onClick={handleClick}
          sx={{ cursor: 'pointer' }}
        >
          {newDate}
        </Typography>
        <Button
          data-cy="increment-year"
          onClick={incrementYear}
          sx={{ minWidth: '30px', color: '#fff' }}
        >
          <ArrowRight data-cy="arrow-right" />
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'year-selection-menu',
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            style: {
              maxHeight: 250,
              overflowY: 'auto',
            },
          }}
        >
          <Box sx={{ p: 1 }}>
            {yearsInRows.map((row, rowIndex) => (
              <Grid container key={rowIndex} spacing={2}>
                {row.map(year => (
                  <Grid item xs={4} key={year}>
                    <MenuItem onClick={() => handleYearSelect(year)}>
                      {year}
                    </MenuItem>
                  </Grid>
                ))}
              </Grid>
            ))}
          </Box>
        </Menu>
      </Box>
    </Tooltip>
  );
};

export default TimePeriod;
